import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import { useGetDefaultZeroCost } from '../../pages/settings/payment-and-subscription/hooks/use-plan-details';
import { useGetSubscriptionCurrencyCode } from '../../pages/settings/payment-and-subscription/hooks/use-get-subscription-currency-code';

import { AddOns } from '../../constants/add-ons';
import Global from '../../store/global-selectors';
import { getReturnRabbitAddOn } from '../../pages/settings/api/add-ons-api';

export const useBuildMarketplaceAddOns = () => {
  const { marketplaceAddOns = emptyArray } = useSelector(Global.selectSellerSubscriptionPlanInfo) || emptyObject;
  const billingCurrencyCode = useGetSubscriptionCurrencyCode();
  const returnRabbitAddOn = getReturnRabbitAddOn();

  return useMemo(() => {
    const addOns = marketplaceAddOns.filter((addOn) => Object.keys(AddOns).includes(addOn.code));
    // Only show ReturnRabbit addon if it's already installed/active
    if (returnRabbitAddOn?.isActive) {
      addOns.push(returnRabbitAddOn);
    }
    const allAddons = addOns.map((addOn) => ({
      ...addOn,
      price: {
        ...addOn.price,
        code: billingCurrencyCode,
      },
    }));

    const enabledAddons = allAddons.filter(({ isActive }) => isActive);
    const disabledAddons = allAddons.filter(({ isActive }) => !isActive);

    return {
      addOns: allAddons,
      enabledAddons,
      disabledAddons,
    };
  }, [billingCurrencyCode, marketplaceAddOns, returnRabbitAddOn]);
};

export const useBuildMarketplaceAddOnByCode = (addOnCode) => {
  const { addOns } = useBuildMarketplaceAddOns();
  const addOn = addOns.find(({ code }) => code === addOnCode);
  if (addOn) {
    return addOn;
  }

  return null;
};

/**
 * @typedef AddOnFeature
 * @property {string} addOnCode
 * @property {string[]} featureIds
 * @property {object} price
 */

/**
 * @returns {AddOnFeature[]}
 * this function will return a master list of addOn and Feature codes
 * that exist in our system and their corresponding Recurly price
 *
 * AddOnFeaturePropType - ss-ui-client/src/prop-types/add-on-feature.js
 * {
    addOnCode: PropTypes.oneOf(Object.keys(AddOns)),
    featureIds: PropTypes.arrayOf(Object.keys(Features)),
    price: CurrencyPropType,
  }
 */
export const useBuildAddOnFeatures = () => {
  const { addOnFeatures = emptyArray } = useSelector(Global.selectSellerSubscriptionPlanInfo) || emptyObject;
  const billingCurrencyCode = useGetSubscriptionCurrencyCode();
  const zeroDollarPrice = useGetDefaultZeroCost();
  const returnRabbitAddOn = getReturnRabbitAddOn();
  return useMemo(() => {
    const mappedAddOnFeatures = addOnFeatures.map((feature) => {
      return {
        ...feature,
        // the BE return a -1 for price if there is no price
        price: feature.price.value === -1 ? zeroDollarPrice : { ...feature.price, code: billingCurrencyCode },
      };
    });

    if (returnRabbitAddOn?.isActive) {
      mappedAddOnFeatures.push({
        addOnCode: returnRabbitAddOn.code,
        featureIds: [returnRabbitAddOn.code],
        price: {
          value: returnRabbitAddOn.price.value,
          code: billingCurrencyCode,
        },
      });
    }

    return mappedAddOnFeatures;
  }, [addOnFeatures, billingCurrencyCode, returnRabbitAddOn, zeroDollarPrice]);
};
